import {
  createContext,
  useContext,
  useState,
  useCallback,
  useId,
  type ReactNode,
  type ButtonHTMLAttributes,
} from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as MenuPrimitive from "@radix-ui/react-dropdown-menu";
import {
  ChevronDown,
  Check,
  X,
  Loader2,
  MoreHorizontal,
  Info,
  AlertCircle,
} from "lucide-react";
import type { Actor } from "@vibe/shared";
import { agentAvatarUrl } from "../avatars";
import { usePageScrollLock } from "../hooks/usePageScrollLock";
export function Button({
  children,
  variant = "default",
  busy = false,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "primary" | "danger" | "ghost";
  busy?: boolean;
}) {
  return (
    <button
      {...props}
      disabled={busy || props.disabled}
      className={`button ${variant} ${props.className || ""}`}
    >
      {busy ? <Loader2 size={14} className="spin" /> : null}
      {children}
    </button>
  );
}
export function IconButton({
  label,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      {...props}
      aria-label={label}
      title={label}
      className={`icon-button ${props.className || ""}`}
    >
      {children}
    </button>
  );
}
export function Avatar({
  actor,
  size = 28,
}: {
  actor: Pick<Actor, "name" | "avatar" | "kind">;
  size?: number;
}) {
  const src =
    actor.avatar === "viby-v1" || actor.avatar?.startsWith("agent-")
      ? agentAvatarUrl(actor.avatar)
      : actor.avatar
        ? `/api/v1/avatars/${actor.avatar}`
        : null;
  return (
    <span
      className={`avatar ${actor.kind}`}
      style={{ width: size, height: size, fontSize: size * 0.34 }}
      title={actor.name}
    >
      {src ? (
        <img src={src} alt={actor.name} />
      ) : (
        actor.name
          .split(" ")
          .map((x) => x[0])
          .slice(0, 2)
          .join("")
          .toUpperCase()
      )}
    </span>
  );
}
export function Select({
  disabled = false,
  value,
  onChange,
  options,
  placeholder = "Select…",
  label,
  className = "",
}: {
  disabled?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; icon?: ReactNode }[];
  placeholder?: string;
  label?: string;
  className?: string;
}) {
  return (
    <SelectPrimitive.Root
      disabled={disabled}
      value={value || "__empty"}
      onValueChange={(v) => onChange(v === "__empty" ? "" : v)}
    >
      <SelectPrimitive.Trigger
        aria-label={label || placeholder}
        className={`select ${className}`}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon>
          <ChevronDown size={13} />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={5}
          className="select-content"
        >
          <SelectPrimitive.Viewport>
            {options.map((o) => (
              <SelectPrimitive.Item
                value={o.value || "__empty"}
                key={o.value}
                className="select-item"
              >
                <SelectPrimitive.ItemText>
                  {o.icon}
                  {o.label}
                </SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator>
                  <Check size={14} />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
export function MultiSelect({
  values,
  onChange,
  options,
  placeholder = "Select…",
  label,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  label?: string;
}) {
  const selected = new Set(values);
  const summary = values.length
    ? values.length === 1
      ? options.find((option) => option.value === values[0])?.label || values[0]
      : `${values.length} models selected`
    : placeholder;
  return (
    <MenuPrimitive.Root>
      <MenuPrimitive.Trigger
        aria-label={label || placeholder}
        className="select multi-select"
      >
        <span>{summary}</span>
        <ChevronDown size={13} />
      </MenuPrimitive.Trigger>
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content
          className="menu-content multi-select-content"
          sideOffset={5}
          align="start"
        >
          {options.map((option) => (
            <MenuPrimitive.CheckboxItem
              key={option.value}
              checked={selected.has(option.value)}
              className="menu-item multi-select-item"
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={(checked) =>
                onChange(
                  checked
                    ? [...values, option.value]
                    : values.filter((value) => value !== option.value),
                )
              }
            >
              <span className="multi-select-check">
                <MenuPrimitive.ItemIndicator>
                  <Check size={12} />
                </MenuPrimitive.ItemIndicator>
              </span>
              {option.label}
            </MenuPrimitive.CheckboxItem>
          ))}
        </MenuPrimitive.Content>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  );
}
export function Modal({
  open,
  onClose,
  title,
  description,
  action,
  navigation,
  children,
  size = "medium",
  stacked = false,
  onOpenAutoFocus,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  action?: ReactNode;
  navigation?: ReactNode;
  children: ReactNode;
  size?: "small" | "medium" | "large" | "fullscreen";
  stacked?: boolean;
  onOpenAutoFocus?: (event: Event) => void;
}) {
  usePageScrollLock(open);
  const descriptionId = useId();

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={`dialog-overlay${stacked ? " stacked" : ""}`}
        />
        <DialogPrimitive.Content
          className={`dialog ${size}${stacked ? " stacked" : ""}`}
          aria-describedby={description ? descriptionId : undefined}
          onOpenAutoFocus={onOpenAutoFocus}
        >
          <div className="dialog-heading">
            <div>
              <DialogPrimitive.Title>{title}</DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description id={descriptionId}>
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
            <div className="dialog-heading-actions">
              {action}
              <DialogPrimitive.Close asChild>
                <IconButton label="Close dialog">
                  <X size={18} />
                </IconButton>
              </DialogPrimitive.Close>
            </div>
          </div>
          {navigation}
          <div className="dialog-body">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
export function Menu({
  items,
}: {
  items: {
    label: string;
    onClick: () => void;
    danger?: boolean;
    disabled?: boolean;
  }[];
}) {
  return (
    <MenuPrimitive.Root>
      <MenuPrimitive.Trigger asChild>
        <IconButton label="More actions">
          <MoreHorizontal size={18} />
        </IconButton>
      </MenuPrimitive.Trigger>
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content
          className="menu-content"
          sideOffset={5}
          align="end"
        >
          {items.map((item, i) => (
            <MenuPrimitive.Item
              disabled={item.disabled}
              className={`menu-item ${item.danger ? "danger" : ""}`}
              key={i}
              onSelect={item.onClick}
            >
              {item.label}
            </MenuPrimitive.Item>
          ))}
        </MenuPrimitive.Content>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  );
}
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
export function Toggle({
  label,
  checked,
  onChange,
  description,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <label className="toggle-field">
      <span>
        <strong>{label}</strong>
        {description && <small>{description}</small>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        className={`toggle ${checked ? "checked" : ""}`}
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </label>
  );
}
export function Empty({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-icon">{icon}</div>
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}
export function Loading() {
  return (
    <div className="loading">
      <Loader2 size={20} className="spin" />
      Loading…
    </div>
  );
}
export function ErrorBox({ error }: { error: unknown }) {
  return error ? (
    <div className="error" role="alert">
      <AlertCircle size={15} />
      {error instanceof Error ? error.message : String(error)}
    </div>
  ) : null;
}
const ToastContext = createContext<(message: string, error?: boolean) => void>(
  () => {},
);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<
    { id: number; text: string; error: boolean }[]
  >([]);
  const toast = useCallback((text: string, error = false) => {
    const id = Date.now() + Math.random();
    setItems((p) => [...p, { id, text, error }]);
    setTimeout(() => setItems((p) => p.filter((x) => x.id !== id)), 5000);
  }, []);
  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((x) => (
          <div className={`toast ${x.error ? "error" : ""}`} key={x.id}>
            {x.error ? <AlertCircle size={16} /> : <Check size={16} />}
            <span>{x.text}</span>
            <IconButton
              label="Dismiss notification"
              onClick={() => setItems((p) => p.filter((y) => y.id !== x.id))}
            >
              <X size={14} />
            </IconButton>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
export const useToast = () => useContext(ToastContext);
